<template>
  <button
    v-if="product"
    ref="button"
    class="wishlist-button wk-button"
    :class="{ [`wishlist-button--${template}`]: true, [$class('disabled')]: disabled }"
    type="button"
    :data-product-handle="product?.handle"
    :data-variant-id="variant?.id"
    :disabled="disabled"
    @click.prevent="handleClick"
  >
    <span class="wishlist-button__wishlist-icon">
      <span
        class="button__label text-body-2xs-underline"
        v-text="$string('cart.line_item.add_to_wishlist')"
      />

      <icon-wishlist />
    </span>
    <span
      class="wishlist-button__wishlist-icon-filled"
      :class="{ [$class('hidden')]: true }"
    >
      <span
        class="button__label text-body-2xs-underline"
        v-text="$string('cart.line_item.remove_from_wishlist')"
      />

      <icon-wishlist-fill />
    </span>
  </button>
</template>

<script>

/**
 * Vue: Wishlist button (wishlist-button)
 * -----------------------------------------------------------------------------
 * Button to add/remove product from wishlist.
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {String} template - `card`, `form`, or `product`.
 * @param {Boolean|Object} variant - Specified or default variant.
 *
 */
import { addToWishlist as DYAddToWishlistEvent } from '~/helpers/dynamic-yield'
import IconWishlist from '~icons/general/wishlist.svg'
import IconWishlistFill from '~icons/general/wishlist-fill.svg'

export default {
  name: 'WishlistButton',

  components: {
    IconWishlist,
    IconWishlistFill,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    template: {
      type: String,
      default: 'card',
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['click'],

  data() {
    return {
      key: 'cnvs-wishlist-data',
    }
  },

  methods: {

    /**
     * Manually trigger click in order to remove item from wishlist
     * page when item is added to cart
     */
    triggerClick() {
      this.$refs.button.click()
    },

    /**
     * Save product data to localStorage.
     * - Collection and badge data not available in wishlist.
     */
    handleClick() {
      if (this.disabled) {
        return
      }

      const productId = this.product?.id

      const details = {
        metafields: this.product?.metafields,
      }

      const data = {
        [productId]: details,
      }

      const storage = localStorage.getItem(this.key)

      if (this.variant) {
        DYAddToWishlistEvent(this.variant)
      }

      /**
       * If storage doesn't exist then write directly.
       */
      if (!storage) {
        cnvs.LocalStorage(this.key, JSON.stringify(data))
        return
      }

      /**
       * If it does then update with new data.
       */
      const json = JSON.parse(storage)
      json[productId] = details
      cnvs.LocalStorage(this.key, JSON.stringify(json))

      this.$emit('click')
    },
  },
}
</script>

<style lang="scss">
@import './wishlist-button';
</style>
